import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  ArrowLeft,
  Search, 
  Filter, 
  RefreshCw,
  Building2,
  MapPin,
  Shield,
  Award,
  Loader2,
  AlertCircle,
  Users,
  Star
} from 'lucide-react'
import apiService from '../../services/api'
import { toast } from 'react-toastify'

interface GovVendor {
  id: string
  companyName: string
  locationCity?: string
  locationState?: string
  avatar?: string
  memberSince: string
  isGovCertified: boolean
  cageCode?: string
  dunsNumber?: string
  gsaScheduleId?: string
  certifications?: string[]
  approvedAgencies?: string[]
  completedJobs: number
  govContractsCompleted: number
}

const GovVendorsPage: React.FC = () => {
  const [vendors, setVendors] = useState<GovVendor[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchVendors()
  }, [])

  const fetchVendors = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Use the existing enterprise vendors endpoint with govCertifiedOnly=true
      const response = await apiService.get('/api/enterprise/vendors?govCertifiedOnly=true&limit=50') as any
      
      if (response?.success) {
        // The API returns { data: [...], pagination: {...} }
        const vendorsData = response.data || []
        setVendors(vendorsData)
      } else {
        setError('Failed to fetch government vendors')
        toast.error('Failed to fetch government vendors')
      }
    } catch (err) {
      console.error('Error fetching government vendors:', err)
      setError('Failed to fetch government vendors')
      toast.error('Failed to fetch government vendors')
    } finally {
      setLoading(false)
    }
  }

  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = vendor.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (vendor.locationCity && vendor.locationCity.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (vendor.locationState && vendor.locationState.toLowerCase().includes(searchTerm.toLowerCase()))
    
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'certified' && vendor.isGovCertified) ||
                         (filterStatus === 'active' && vendor.isGovCertified)
    
    return matchesSearch && matchesFilter
  })

  const VendorCard: React.FC<{ vendor: GovVendor }> = ({ vendor }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start space-x-4">
        <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center flex-shrink-0">
          {vendor.avatar ? (
            <img
              src={vendor.avatar}
              alt={vendor.companyName}
              className="w-16 h-16 rounded-lg object-cover"
            />
          ) : (
            <Building2 className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                {vendor.companyName}
              </h3>
            </div>
            
            <div className="flex items-center space-x-2">
              {vendor.isGovCertified && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  <Shield className="w-3 h-3 mr-1" />
                  Certified
                </span>
              )}
            </div>
          </div>
          
          <div className="mt-3 space-y-2">
            {(vendor.locationCity || vendor.locationState) && (
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <MapPin className="w-4 h-4 mr-1" />
                {vendor.locationCity && vendor.locationState 
                  ? `${vendor.locationCity}, ${vendor.locationState}`
                  : vendor.locationCity || vendor.locationState}
              </div>
            )}
            
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <Users className="w-4 h-4 mr-1" />
              {vendor.govContractsCompleted} gov contracts completed
            </div>
            
            {vendor.cageCode && (
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <Award className="w-4 h-4 mr-1" />
                CAGE: {vendor.cageCode}
              </div>
            )}
          </div>
          
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <Star className="w-4 h-4 mr-1" />
              Member since {new Date(vendor.memberSince).getFullYear()}
            </div>
            
            <Link
              to={`/gov/vendor/${vendor.id}`}
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
            >
              View Details →
            </Link>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4 mb-6">
            <Link 
              to="/gov/dashboard"
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Dashboard
            </Link>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                Government Certified Vendors
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Browse and connect with certified government contractors
              </p>
            </div>
            
            <div className="mt-4 lg:mt-0">
              <button
                onClick={fetchVendors}
                disabled={loading}
                className="btn btn-secondary flex items-center space-x-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search vendors by name, email, or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="input w-auto"
                >
                  <option value="all">All Vendors</option>
                  <option value="certified">Certified Only</option>
                  <option value="active">Active Only</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing {filteredVendors.length} of {vendors.length} vendors
          </p>
        </div>

        {/* Vendor Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <Loader2 className="w-8 h-8 text-primary-600 animate-spin mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Loading certified vendors...</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <AlertCircle className="w-8 h-8 text-red-500 mb-4" />
              <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
              <button
                onClick={fetchVendors}
                className="btn btn-primary"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : filteredVendors.length === 0 ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <Users className="w-8 h-8 text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                {searchTerm || filterStatus !== 'all' 
                  ? 'No vendors match your search criteria.' 
                  : 'No certified vendors found.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVendors.map((vendor) => (
              <VendorCard key={vendor.id} vendor={vendor} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default GovVendorsPage